import { Drawer } from '@mui/material';
import { AnimatePresence } from 'motion/react';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { isStaleCart } from '@/clients/storefront/cartService';
import { getCart } from '@/clients/storefront/storefrontClient';
import useSunrunShopStore, {
  useSunrunShopActions,
} from '@/data-store/zustand/hooks/useSunrunShopStore';
import useStyles from './Cart.styles';
import EmptyCart from './EmptyCart';
import FilledCart from './FilledCart';

const Cart: React.FC = () => {
  const { cart, isCartOpen } = useSunrunShopStore('cart', 'isCartOpen');
  const { setIsCartOpen, setCart, setCartId } = useSunrunShopActions();
  const { classes } = useStyles();
  const router = useRouter();

  const handleClose = () => setIsCartOpen(false);
  const isEmpty = !cart?.lines.edges.length;

  useEffect(() => {
    setIsCartOpen(false);
  }, [setIsCartOpen]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to fire on route change
  useEffect(() => {
    const fetchCart = async () => {
      if (!cart?.id) return;

      const currentCart = await getCart(cart.id);
      if (currentCart) {
        setCart(currentCart);
      } else {
        setCartId(null);
      }
    };

    fetchCart();
  }, [cart?.id, router.asPath, setCartId, setCart]);

  useEffect(() => {
    if (isCartOpen && cart?.id) {
      getCart(cart.id).then((updatedCart) => {
        if (updatedCart) {
          setCart(updatedCart);
        }
      });
    }
  }, [isCartOpen, cart?.id, setCart]);

  useEffect(() => {
    if (cart && isStaleCart(cart?.updatedAt)) {
      setCart(null);
    }
  }, [cart, setCart]);

  return (
    <Drawer
      anchor='right'
      keepMounted
      open={isCartOpen}
      onClose={handleClose}
      PaperProps={{ className: classes.paper }}
      slotProps={{
        backdrop: { className: classes.backdrop },
        root: { className: classes.root },
      }}
    >
      <AnimatePresence mode='wait' initial={false}>
        {isEmpty ? <EmptyCart key='empty' /> : <FilledCart key='filled' />}
      </AnimatePresence>
    </Drawer>
  );
};

export default Cart;
